import React, { useState, useEffect } from 'react';
import ErpTemplate from '../../../components/ErpTemplate';
import { FaSearch, FaPen, FaPlus, FaCreditCard, FaUniversity, FaTimes, FaBalanceScale } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import moment from 'moment';
import Data from '../../../components/Data';
import FaturaCartao from '../../../components/Financeiro/FaturaCartao';
import '../../../css/financeiro-parcela-pagar.css';

export default function PainelCaixa() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    // Saldo de abertura / conciliação (2026-08-03): o saldo passa a ter fonte
    // única no extrato. Restrito ao Financeiro/Admin/Diretor — a mesma trava
    // existe no backend; aqui é só para não mostrar botão que vai ser recusado.
    const podeAjustarSaldo = ['financeiro', 'gerentefinanceiro', 'admin', 'diretor'].includes(perfil);
    const [modalSaldo, setModalSaldo] = useState(null); // { caixa, valor, data, observacao }
    const [salvandoSaldo, setSalvandoSaldo] = useState(false);

    const [caixas, setCaixas] = useState([]);
    const [caixaSelecionado, setCaixaSelecionado] = useState(null);
    const [movimentos, setMovimentos] = useState([]);
    const [saldos, setSaldos] = useState({});
    const [buscando, setBuscando] = useState(false);
    const [filtro, setFiltro] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    // Cartão selecionado: "Extrato" (movimentos) ou "Faturas" (ciclo pelo dia de corte — item 15).
    const [visao, setVisao] = useState('extrato');

    const buscarCaixas = () => {
        api({ method: 'get', action: '/financeiroCaixa', token: token, params: {} })
            .then(response => {
                const lista = response.data.data || [];
                setCaixas(lista);
                if (lista.length > 0) selecionarCaixa(lista[0]);
            })
            .catch(() => {});
    };

    const buscarSaldos = () => {
        api({ method: 'get', action: '/financeiroMovimentoCaixa/saldos', token: token, params: {} })
            .then(response => setSaldos(response.data.data || {}))
            .catch(() => {});
    };

    // Informa o saldo REAL; o backend lança a DIFERENÇA no extrato (conciliação).
    // Nada é sobrescrito — o histórico fica auditável.
    const salvarAjusteSaldo = () => {
        if (!modalSaldo) return;
        const bruto = String(modalSaldo.valor ?? '').trim();
        if (bruto === '') { toast.error('Informe o saldo real do caixa.'); return; }
        setSalvandoSaldo(true);
        api({
            method: 'put',
            action: '/financeiroCaixa/' + modalSaldo.caixa.id + '/ajustarSaldo',
            token: token,
            data: {
                saldo: bruto,
                data: modalSaldo.data ? moment(modalSaldo.data).format('YYYY-MM-DD HH:mm:ss') : null,
                observacao: modalSaldo.observacao || '',
            }
        })
            .then(response => {
                const d = response.data?.data || {};
                if (d.ajustado === false) {
                    toast.info(d.mensagem || 'O saldo informado já é o saldo atual.');
                } else {
                    const dif = parseFloat(d.diferenca || 0);
                    // formatação igual à do resto da tela (toLocaleString pt-BR)
                    toast.success((d.abertura ? 'Saldo de abertura lançado' : 'Saldo ajustado')
                        + ': ' + (dif >= 0 ? '+' : '−') + ' '
                        + Math.abs(dif).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
                }
                setModalSaldo(null);
                buscarSaldos();
                if (caixaSelecionado && caixaSelecionado.id === modalSaldo.caixa.id) {
                    buscarMovimentos(caixaSelecionado.id);
                }
            })
            .catch(error => {
                const m = error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao ajustar o saldo.';
                toast.error(m);
            })
            .finally(() => setSalvandoSaldo(false));
    };

    const buscarMovimentos = (idCaixa, filtroOverride = null) => {
        setBuscando(true);
        setPage(1);
        const f = filtroOverride !== null ? filtroOverride : filtro;
        const params = { caixa: idCaixa };
        if (f.dataInicio) params.dataInicio = moment(f.dataInicio).format('YYYY-MM-DD');
        if (f.dataFim) params.dataFim = moment(f.dataFim).format('YYYY-MM-DD');
        api({ method: 'get', action: '/financeiroMovimentoCaixa', token: token, params })
            .then(response => { setMovimentos(response.data.data || []); setBuscando(false); })
            .catch(() => { setBuscando(false); });
    };

    const selecionarCaixa = (cx) => {
        setCaixaSelecionado(cx);
        if (!(cx && parseFloat(cx.cartaolimite) > 0)) setVisao('extrato');
        if (cx && cx.id) buscarMovimentos(cx.id);
    };

    useEffect(() => {
        buscarCaixas();
        buscarSaldos();
        // eslint-disable-next-line
    }, []);

    const total = movimentos.length;
    const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
    const paginados = movimentos.slice((page - 1) * pageSize, page * pageSize);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div><h1 className="fin-pp__title">Caixas</h1></div>
                    <div className="fin-pp__actions">
                        <Link to="/cadastroCaixa" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                            <FaPlus size={14} /> Novo Caixa
                        </Link>
                    </div>
                </div>

                {/* Strip horizontal de caixas — scroll quando há muitos */}
                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, marginBottom: 16 }}>
                    {caixas.length === 0 && (
                        <span className="fin-pp__cell-muted" style={{ padding: '12px 0' }}>Nenhum caixa cadastrado</span>
                    )}
                    {caixas.map((cx) => {
                        const ehCartao = parseFloat(cx.cartaolimite) > 0;
                        const saldoDisponivel = parseFloat(cx.cartaosaldo) || 0;   // cartão: limite disponível
                        const limite = parseFloat(cx.cartaolimite) || 0;
                        const saldoMovimento = parseFloat(saldos[cx.id] || 0);     // razão (movimentos) — real p/ banco
                        // Banco: o número principal é o saldo do RAZÃO (muda ao pagar/receber),
                        // não o cartaosaldo estático. Cartão: mantém o limite disponível.
                        const saldoPrincipal = ehCartao ? saldoDisponivel : saldoMovimento;
                        const pctDisponivel = ehCartao && limite > 0
                            ? Math.min(100, Math.round((saldoDisponivel / limite) * 100))
                            : null;
                        const corSaldo = !ehCartao
                            ? (saldoMovimento < 0 ? 'var(--fin-dg)' : saldoMovimento > 0 ? 'var(--fin-ac)' : 'var(--fin-mt)')
                            : (pctDisponivel >= 50 ? 'var(--fin-ok)' : pctDisponivel >= 20 ? 'var(--fin-wn)' : 'var(--fin-dg)');
                        const ativo = caixaSelecionado && caixaSelecionado.id === cx.id;
                        return (
                            <div
                                key={cx.id}
                                className="fin-pp__card"
                                onClick={() => selecionarCaixa(cx)}
                                style={{
                                    flex: '0 0 190px',
                                    cursor: 'pointer',
                                    borderLeft: '3px solid ' + (ehCartao ? '#6610f2' : 'var(--fin-ac)'),
                                    outline: ativo ? '2px solid var(--fin-ac)' : 'none',
                                    outlineOffset: 1,
                                    padding: '10px 12px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                        {ehCartao
                                            ? <FaCreditCard size={12} color="#6610f2" style={{ flexShrink: 0 }} />
                                            : <FaUniversity size={12} color="var(--fin-ac)" style={{ flexShrink: 0 }} />
                                        }
                                        <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--fin-tx)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cx.nome}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                        {cx.principal === 'sim' && (
                                            <span className="fin-pp__minitag is-info">P</span>
                                        )}
                                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                                            {podeAjustarSaldo && (
                                                <button type="button" className="fin-pp__rowbtn" style={{ width: 24, height: 24 }}
                                                    title="Informar saldo real (abertura / conciliação)"
                                                    onClick={() => setModalSaldo({ caixa: cx, valor: '', data: null, observacao: '' })}>
                                                    <FaBalanceScale size={11} />
                                                </button>
                                            )}
                                            <Link to={"/cadastroCaixa/" + cx.id} className="fin-pp__rowbtn" style={{ width: 24, height: 24 }}>
                                                <FaPen size={11} />
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 4 }}>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: corSaldo, display: 'block', lineHeight: 1.2 }}>
                                        {ehCartao
                                            ? (saldoDisponivel > 0
                                                ? saldoDisponivel.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                : <span style={{ fontSize: 11, color: 'var(--fin-mt)' }}>— atualizar saldo</span>)
                                            : saldoPrincipal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                        }
                                    </span>
                                    <span style={{ fontSize: 10, color: 'var(--fin-mt)' }}>
                                        {ehCartao ? 'disponível cartão' : 'saldo em conta'}
                                    </span>
                                </div>

                                {ehCartao && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--fin-mt)', marginTop: 4, flexWrap: 'wrap' }}>
                                        {cx.diaCorteCartao && <span>corte {cx.diaCorteCartao}</span>}
                                        {pctDisponivel !== null && (
                                            <span className={'fin-pp__minitag ' + (pctDisponivel >= 50 ? 'is-ok' : pctDisponivel >= 20 ? 'is-warn' : 'is-danger')}>
                                                {pctDisponivel}%
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Cartão: mostra também o contábil (razão) além do limite disponível.
                                    Banco: o principal já é o contábil, não repete. */}
                                {ehCartao && (
                                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--fin-sf1)' }}>
                                        <span style={{ fontSize: 10, color: 'var(--fin-mt)' }}>
                                            contábil {saldoMovimento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Cartão: alterna Extrato | Faturas */}
                {caixaSelecionado && parseFloat(caixaSelecionado.cartaolimite) > 0 && (
                    <div className="fin-pp__tabs" style={{ marginBottom: 14 }}>
                        <button type="button" className={'fin-pp__tab' + (visao === 'extrato' ? ' is-active' : '')} onClick={() => setVisao('extrato')}>Extrato</button>
                        <button type="button" className={'fin-pp__tab' + (visao === 'faturas' ? ' is-active' : '')} onClick={() => setVisao('faturas')}>Faturas</button>
                    </div>
                )}

                {visao === 'faturas' && caixaSelecionado && parseFloat(caixaSelecionado.cartaolimite) > 0 ? (
                    <FaturaCartao cartao={caixaSelecionado} onGerada={() => buscarSaldos()} />
                ) : (<>
                {/* Filtro de movimentos — 1 linha */}
                <form
                    className="fin-pp__card"
                    style={{ marginBottom: 16, padding: '10px 16px', display: 'flex', flexWrap: 'nowrap', gap: 12, alignItems: 'flex-end' }}
                    onSubmit={(e) => { if (caixaSelecionado && caixaSelecionado.id) buscarMovimentos(caixaSelecionado.id); e.preventDefault(); }}
                >
                    <div style={{ flex: '0 0 160px' }}>
                        <Data
                            label="Data início"
                            value={filtro.dataInicio}
                            onChange={date => setFiltro({ ...filtro, dataInicio: date })}
                        />
                    </div>
                    <div style={{ flex: '0 0 160px' }}>
                        <Data
                            label="Data fim"
                            value={filtro.dataFim}
                            onChange={date => setFiltro({ ...filtro, dataFim: date })}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={!(caixaSelecionado && caixaSelecionado.id)}>
                        <FaSearch size={13} /> Filtrar
                    </button>
                    {(filtro.dataInicio || filtro.dataFim) && (
                        <button type="button" className="fin-pp__btn" onClick={() => {
                            setFiltro({});
                            if (caixaSelecionado && caixaSelecionado.id) buscarMovimentos(caixaSelecionado.id, {});
                        }}>
                            Limpar
                        </button>
                    )}
                </form>

                {/* Tabela de movimentos */}
                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    {caixaSelecionado && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--fin-sf1)' }}>
                            {parseFloat(caixaSelecionado.cartaolimite) > 0
                                ? <FaCreditCard size={14} color="#6610f2" />
                                : <FaUniversity size={14} color="var(--fin-ac)" />
                            }
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fin-tx)' }}>
                                Movimentos — {caixaSelecionado.nome}
                            </span>
                        </div>
                    )}
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 140 }}>Data/Hora</th>
                                    <th>Conta</th>
                                    <th style={{ width: 130 }}>Espécie</th>
                                    <th>Descrição</th>
                                    <th style={{ width: 120, textAlign: 'right' }}>Entrada</th>
                                    <th style={{ width: 120, textAlign: 'right' }}>Saída</th>
                                    <th style={{ width: 130, textAlign: 'right' }}>Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buscando
                                    ? <tr><td colSpan={7} className="fin-pp__empty">Buscando...</td></tr>
                                    : paginados.length === 0
                                        ? <tr><td colSpan={7} className="fin-pp__empty">
                                            {caixaSelecionado ? 'Nenhum movimento no período' : 'Selecione um caixa acima'}
                                          </td></tr>
                                        : paginados.map((row, i) => {
                                            const entrada = parseFloat(row.valorentrada || 0);
                                            const saida = parseFloat(row.valorsaida || 0);
                                            const saldo = parseFloat(row.saldo || 0);
                                            return (
                                                <tr key={row.id != null ? row.id : i}>
                                                    <td className="fin-pp__cell-muted">{moment(row.datahora).format('DD/MM/YYYY HH:mm')}</td>
                                                    <td className="fin-pp__cell-muted">{row.contaNome != null ? row.contaNome : ''}</td>
                                                    <td className="fin-pp__cell-muted">{row.especieNome != null ? row.especieNome : ''}</td>
                                                    <td>
                                                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {row.descricao || ''}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {entrada > 0
                                                            ? <span style={{ color: 'var(--fin-ok)', fontWeight: 600 }}>{entrada.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                            : <span style={{ color: 'var(--fin-mt)' }}>—</span>
                                                        }
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {saida > 0
                                                            ? <span style={{ color: 'var(--fin-dg)', fontWeight: 600 }}>{saida.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                            : <span style={{ color: 'var(--fin-mt)' }}>—</span>
                                                        }
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <span style={{ fontWeight: 600, color: saldo >= 0 ? 'var(--fin-ac)' : 'var(--fin-dg)' }}>
                                                            {saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                }
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{total} movimentos</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select className="fin-pp__select" style={{ height: 34 }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage(p => p + 1)}>Próxima</button>
                        </div>
                    </div>
                </div>
                </>)}
            </div>

            {/* Modal: informar saldo real (abertura ou conciliação) */}
            {modalSaldo && (
                <div className="fin-pp fin-pp__sheet-backdrop" onClick={() => !salvandoSaldo && setModalSaldo(null)}>
                    <div className="fin-pp__sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
                        <div className="fin-pp__sheet-head">
                            <span className="fin-pp__sheet-head-ic"><FaBalanceScale size={17} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h3 className="fin-pp__sheet-title">Informar saldo real</h3>
                                <p className="fin-pp__sheet-sub">{modalSaldo.caixa.nome}</p>
                            </div>
                            <button type="button" className="fin-pp__sheet-close" onClick={() => setModalSaldo(null)}><FaTimes size={16} /></button>
                        </div>
                        <div className="fin-pp__sheet-body" style={{ padding: '16px 18px' }}>
                            <div style={{ background: '#eef4ff', border: '1px solid #cfe0ff', borderRadius: 6, padding: '10px 12px', marginBottom: 14, fontSize: '0.82rem', color: '#1e4a8a' }}>
                                Saldo pelo extrato hoje: <strong>
                                    {parseFloat(saldos[modalSaldo.caixa.id] || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </strong>
                                <div style={{ marginTop: 4 }}>
                                    O sistema lança a <strong>diferença</strong> como movimento — nada é sobrescrito e o histórico fica registrado.
                                </div>
                            </div>

                            <label className="form-label small fw-bold">
                                Saldo real <span style={{ color: 'var(--fin-dg)' }}>*</span>
                            </label>
                            <input type="text" className="form-control form-control-sm mb-3"
                                placeholder="ex: 100000,00" autoFocus
                                value={modalSaldo.valor}
                                onChange={e => setModalSaldo({ ...modalSaldo, valor: e.target.value })} />

                            <label className="form-label small fw-bold">Data do lançamento</label>
                            <div className="mb-3">
                                <Data value={modalSaldo.data} onChange={d => setModalSaldo({ ...modalSaldo, data: d })} />
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>Em branco = agora.</div>
                            </div>

                            <label className="form-label small fw-bold">Observação</label>
                            <input type="text" className="form-control form-control-sm"
                                placeholder="ex: conciliação com extrato bancário"
                                value={modalSaldo.observacao}
                                onChange={e => setModalSaldo({ ...modalSaldo, observacao: e.target.value })} />
                        </div>
                        <div className="d-flex justify-content-end gap-2" style={{ padding: '12px 18px', borderTop: '1px solid #e9ecef' }}>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={salvandoSaldo}
                                onClick={() => setModalSaldo(null)}>Cancelar</button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--ok fin-pp__btn--sm" disabled={salvandoSaldo}
                                onClick={salvarAjusteSaldo}>
                                {salvandoSaldo ? 'Salvando...' : 'Lançar saldo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
